import { createContext, type ReactNode, useCallback, useContext, useState } from 'react';
import { UNSAFE_PortalProvider } from 'react-aria';
import { createPortal } from 'react-dom';

/**
 * A pane's own overlay layer: an element covering the pane and nothing else,
 * outside its scroll box. In a Workspace pane, side panels, dialogs and every
 * react-aria overlay (HeroUI popovers, menus, selects, modals) render into
 * it, so they open inside the pane — portalled to <body> they opened at the
 * window's edge, over the neighbouring app (Tables' "Filter & sort" sat over
 * the Notes pane).
 *
 * `null` (no provider: the full-page app) means the body, as before.
 * The drive keeps its own, finer layer (page_app/drive/layer.tsx).
 */
const OverlayLayerContext = createContext<HTMLElement | null>(null);

export const useOverlayLayer = () => useContext(OverlayLayerContext);

/** `absolute` inside a layer, `fixed` against the window without one. */
export const overlayPosition = (layer: HTMLElement | null) => (layer ? 'absolute' : 'fixed');

/** Into the pane's layer when there is one, else the body. */
export function Overlay({ children }: { children: ReactNode }) {
  const layer = useOverlayLayer();
  return createPortal(children, layer ?? document.body);
}

/**
 * Gives its children a layer: wrap the pane's frame (not its scroll box —
 * the layer must not scroll away with the content).
 */
export function OverlayLayer({ children, className = '' }: { children: ReactNode; className?: string }) {
  const [layer, setLayer] = useState<HTMLDivElement | null>(null);
  const getContainer = useCallback(() => layer, [layer]);
  return (
    <OverlayLayerContext.Provider value={layer}>
      <UNSAFE_PortalProvider getContainer={layer ? getContainer : null}>
        <div className={`relative ${className}`}>
          {children}
          {/* Covers the pane, passes clicks through except to what is in it. */}
          <div ref={setLayer} data-overlay-layer className="pointer-events-none absolute inset-0 z-[150] *:pointer-events-auto" />
        </div>
      </UNSAFE_PortalProvider>
    </OverlayLayerContext.Provider>
  );
}
